import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import LineFloatingButton from '../../components/feature/LineFloatingButton';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

export default function DisposalPage() {
  const [activeTab, setActiveTab] = useState('home');
  const navigate = useNavigate();

  useSEO({
    title: '長崎不用品整理サービス | みなとサービス - 家庭・オフィス・遺品整理',
    description: '長崎県の不用品整理サービス。家庭の不用品からオフィス用品、遺品整理まで対応。軽トラック1台17,000円〜。即日対応可能。お見積もり無料、明朗会計で安心してご利用いただけます。',
    keywords: '長崎,不用品回収,不用品整理,粗大ゴミ,遺品整理,オフィス用品処分,家具処分,家電リサイクル,長崎県,即日対応',
    canonical: '/disposal',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '不用品整理サービス',
      description: '長崎県の不用品整理サービス。家庭の不用品からオフィス用品、遺品整理まで迅速に回収・処分します。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'みなとサービス',
        telephone: '0120-743-643',
        address: {
          '@type': 'PostalAddress',
          addressRegion: '長崎県',
          addressCountry: 'JP'
        }
      },
      areaServed: {
        '@type': 'Place',
        name: '長崎県'
      },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'PriceSpecification',
          price: '17000',
          priceCurrency: 'JPY',
          description: '基本料金（軽トラック1台分）'
        }
      }
    }
  });

  const services = [
    {
      icon: 'ri-home-4-line',
      title: '家庭の不用品',
      description: '家具、家電、日用品など、ご家庭で不要になったものを回収します。'
    },
    {
      icon: 'ri-building-line',
      title: 'オフィス用品',
      description: 'デスク、チェア、OA機器など、オフィス移転時の不用品を処分します。'
    },
    {
      icon: 'ri-heart-line',
      title: '遺品整理',
      description: '故人の大切な思い出の品を丁寧に整理・処分いたします。'
    },
    {
      icon: 'ri-store-line',
      title: '店舗什器',
      description: '閉店時の什器や備品の撤去・処分をお手伝いします。'
    }
  ];

  const pricingPlans = [
    {
      name: '軽トラック1台分',
      price: '17,000〜',
      capacity: '350kgまで',
      items: ['単身者向け', '小型家具・家電', '段ボール10箱程度']
    },
    {
      name: '家具・家電1点など',
      price: '5,500〜',
      capacity: '単品回収',
      items: ['小型家具1点', '家電製品1点', '少量の不用品']
    }
  ];

  const faqItems = [
    {
      question: 'どんなものでも回収してもらえますか?',
      answer: '家具、家電、日用品など、ほとんどのものを回収可能です。ただし、危険物、医療廃棄物、産業廃棄物などは回収できません。詳しくはお問い合わせください。'
    },
    {
      question: '当日の回収は可能ですか?',
      answer: 'スケジュールに空きがあれば当日対応も可能です。まずはお電話またはLINEでご相談ください。'
    },
    {
      question: '料金はどのように決まりますか?',
      answer: '基本的にトラックの台数で料金が決まります。事前に無料見積もりを行いますので、安心してご依頼いただけます。'
    },
    {
      question: '買取はしてもらえますか?',
      answer: '申し訳ございませんが、当社では買取サービスは行っておりません。不用品の回収・処分のみ対応しております。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/51c842e7e5b7f982c418b38136b67b03.jpg"
            alt="不用品整理サービス"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold mb-3 md:mb-6">
            不用品整理サービス
          </h1>
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl mb-4 md:mb-8 text-white/90 leading-relaxed">
            ご家庭からオフィスまで、<br className="sm:hidden" />
            不要になったものを<br className="hidden sm:block" />
            迅速に回収・処分
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link
              to="/contact"
              className="bg-[#ebe960] text-gray-900 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold text-sm md:text-lg hover:bg-[#d4d34d] transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-mail-line text-lg md:text-xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              対応可能な不用品
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600">
              様々な種類の不用品回収に対応しています
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#f8f6f1] rounded-xl md:rounded-2xl p-4 md:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-[#ebe960] rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-6">
                  <i className={`${service.icon} text-xl sm:text-2xl md:text-3xl text-gray-900`}></i>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 md:py-24 bg-[#f8f6f1]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              料金プラン
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600">
              明朗会計で安心してご利用いただけます
            </p>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto">
            <div className="bg-[#ebe960] rounded-xl md:rounded-2xl p-2.5 md:p-4 mb-5 md:mb-8 text-center">
              <p className="text-gray-900 font-bold text-sm md:text-base lg:text-lg">
                基本料金
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
              {pricingPlans.map((plan, index) => (
                <div
                  key={index}
                  className="bg-[#f8f6f1] rounded-xl md:rounded-3xl p-4 md:p-8 hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-3 md:mb-6">
                    <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                      ¥{plan.price}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 md:mt-8 p-4 md:p-6 bg-[#f8f6f1] rounded-xl md:rounded-2xl">
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose">
                <strong>※料金について</strong>
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                上記は軽トラック1台、作業員1名あたりの<br className="sm:hidden" />
                基本料金の目安です。
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                階段作業、エレベーターなし、<br className="sm:hidden" />
                狭い通路などの条件により<br />
                追加料金が発生する場合があります。
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                また、時期(3月・4月)には<br className="sm:hidden" />
                料金が変動することがございます。
              </p>

              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-5">
                <strong>※家電品目について</strong>
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                エアコン、テレビ、冷蔵庫/冷凍庫、<br className="sm:hidden" />
                洗濯機/乾燥機は<br className="hidden sm:block" />
                別途処分料金が必要となります。
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-4">
                詳しくはお問い合わせください。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              ご利用の流れ
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {[
              { step: '01', title: 'お問い合わせ', desc: '電話・LINE・メールでご連絡ください' },
              { step: '02', title: '無料見積もり', desc: '現地確認または写真で見積もりを作成' },
              { step: '03', title: '日程調整', desc: 'ご都合の良い日時を決定します' },
              { step: '04', title: '回収・処分', desc: '迅速かつ丁寧に作業を行います' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <span className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900">{item.step}</span>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed px-1">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-24 bg-[#f8f6f1]">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              よくある質問
            </h2>
          </div>

          <div className="space-y-3 md:space-y-4">
            {faqItems.map((item, index) => (
              <details
                key={index}
                className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 group cursor-pointer"
              >
                <summary className="flex items-start justify-between font-bold text-gray-900 text-sm md:text-base lg:text-lg list-none cursor-pointer">
                  <span className="pr-3 md:pr-4 leading-relaxed">{item.question}</span>
                  <i className="ri-arrow-down-s-line text-lg md:text-2xl text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <p className="mt-3 md:mt-4 text-gray-600 text-xs md:text-sm lg:text-base leading-relaxed">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-6">
            お気軽にご相談ください。
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-gray-600 mb-6 md:mb-10">
            無料見積もり・ご相談は24時間受付中
          </p>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 md:gap-3 bg-gray-900 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-base sm:text-lg md:text-xl font-bold hover:bg-gray-800 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-fill text-lg sm:text-xl md:text-2xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LineFloatingButton />
    </div>
  );
}
